import React from 'react';
import { Link } from 'react-router-dom';
import { 
  MessageCircle, FileText, Star, QrCode, 
  ArrowRight, Sparkles, Gift, Link2, Calculator, CreditCard,
  Camera, Video, Wand2
} from 'lucide-react';

const aiTools = [
  {
    id: 'virtual-tour',
    name: 'Virtual Tour Creator',
    description: 'Create stunning 360° virtual tours from photos using AI',
    icon: Camera,
    color: 'from-cyan-500 to-blue-600',
    iconColor: 'text-cyan-500',
    path: '/tools/virtual-tour',
    badge: 'AI',
    requiresLogin: true
  },
  {
    id: 'video-ad',
    name: 'AI Video Ad Creator',
    description: 'Transform product photos into video advertisements',
    icon: Video,
    color: 'from-purple-500 to-pink-600',
    iconColor: 'text-purple-500',
    path: '/tools/video-ad',
    badge: 'AI',
    requiresLogin: true
  }
];

const tools = [
  {
    id: 'whatsapp-link',
    name: 'WhatsApp Link Generator',
    description: 'Create shareable WhatsApp links with pre-filled messages',
    icon: MessageCircle,
    color: 'from-green-500 to-green-600',
    iconColor: 'text-green-500',
    path: '/tools/whatsapp-link',
    badge: null
  },
  {
    id: 'invoice',
    name: 'Invoice Generator',
    description: 'Create professional invoices and bills with GST',
    icon: FileText,
    color: 'from-blue-500 to-blue-600',
    iconColor: 'text-blue-500',
    path: '/tools/invoice',
    badge: null
  },
  {
    id: 'testimonials',
    name: 'Testimonial Collector',
    description: 'Collect and display customer reviews beautifully',
    icon: Star,
    color: 'from-yellow-500 to-orange-500',
    iconColor: 'text-yellow-500',
    path: '/tools/testimonials',
    badge: null
  },
  {
    id: 'qr-code',
    name: 'QR Code Generator',
    description: 'Generate QR codes for any text, URL, or contact',
    icon: QrCode,
    color: 'from-purple-500 to-pink-500',
    iconColor: 'text-purple-500',
    path: '/tools/qr-code',
    badge: null
  },
  {
    id: 'bio-link',
    name: 'Bio Link Generator',
    description: 'Create Instagram-style link-in-bio pages',
    icon: Link2,
    color: 'from-pink-500 to-purple-500',
    iconColor: 'text-pink-500',
    path: '/tools/bio-link',
    badge: 'NEW'
  },
  {
    id: 'emi-calculator',
    name: 'EMI Calculator',
    description: 'Calculate loan EMIs and payment schedules instantly',
    icon: Calculator,
    color: 'from-emerald-500 to-teal-500',
    iconColor: 'text-emerald-500',
    path: '/tools/emi-calculator',
    badge: 'NEW'
  },
  {
    id: 'business-card',
    name: 'Digital Business Card',
    description: 'Create shareable digital business cards with QR',
    icon: CreditCard,
    color: 'from-amber-500 to-orange-500',
    iconColor: 'text-amber-500',
    path: '/tools/business-card',
    badge: 'NEW'
  }
];

const FreeTools = () => {
  return (
    <div className="min-h-screen bg-background" data-testid="free-tools-page">
      {/* Hero */}
      <div className="bg-gradient-to-br from-primary/20 via-background to-accent/10 py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-green-500/20 px-4 py-2 rounded-full text-green-500 text-sm font-semibold mb-6">
            <Gift className="w-4 h-4" />
            100% Free Tools
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Free <span className="text-primary">Business Tools</span>
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Powerful tools to grow your business - absolutely free, no signup required!
          </p>
        </div>
      </div>

      {/* AI Tools Section */}
      <div className="max-w-5xl mx-auto px-4 pt-12">
        <div className="flex items-center gap-3 mb-6">
          <Wand2 className="w-6 h-6 text-primary" />
          <h2 className="text-2xl font-bold">AI-Powered Tools</h2>
          <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full font-semibold animate-pulse">
            NEW
          </span>
        </div>
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {aiTools.map((tool) => (
            <Link
              key={tool.id}
              to={tool.path}
              className="glass-card p-6 hover:border-primary/50 transition-all group relative overflow-hidden"
              data-testid={`tool-card-${tool.id}`}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br opacity-10 rounded-bl-full" 
                style={{ background: `linear-gradient(to bottom right, ${tool.color.includes('cyan') ? '#06b6d4' : '#a855f7'}, transparent)` }}
              ></div>
              <div className="flex items-start gap-4">
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${tool.color} flex items-center justify-center flex-shrink-0`}>
                  <tool.icon className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-xl font-bold group-hover:text-primary transition-colors">
                      {tool.name}
                    </h3>
                    <span className="text-[10px] font-bold bg-gradient-to-r from-primary to-purple-500 text-white px-2 py-0.5 rounded-full">
                      {tool.badge}
                    </span>
                  </div>
                  <p className="text-muted-foreground">{tool.description}</p>
                  {tool.requiresLogin && (
                    <p className="text-xs text-muted-foreground mt-2">Requires login</p>
                  )}
                </div>
                <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Free Tools Grid */}
      <div className="max-w-5xl mx-auto px-4 pb-12">
        <div className="flex items-center gap-3 mb-6">
          <Gift className="w-6 h-6 text-green-500" />
          <h2 className="text-2xl font-bold">Free Business Tools</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {tools.map((tool) => (
            <Link
              key={tool.id}
              to={tool.path}
              className="glass-card p-6 hover:border-white/20 transition-all group"
              data-testid={`tool-card-${tool.id}`}
            >
                <div className="flex items-start gap-4">
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${tool.color} flex items-center justify-center flex-shrink-0`}>
                  <tool.icon className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-xl font-bold group-hover:text-primary transition-colors">
                      {tool.name}
                    </h3>
                    {tool.badge ? (
                      <span className="text-[10px] font-bold bg-primary/20 text-primary px-2 py-0.5 rounded-full animate-pulse">
                        {tool.badge}
                      </span>
                    ) : (
                      <span className="text-[10px] font-bold bg-green-500/20 text-green-500 px-2 py-0.5 rounded-full">
                        FREE
                      </span>
                    )}
                  </div>
                  <p className="text-muted-foreground">{tool.description}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
              </div>
            </Link>
          ))}
        </div>

        {/* Features */}
        <div className="mt-16 text-center">
          <h2 className="text-2xl font-bold mb-8">Why Use Our Free Tools?</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="glass-card p-6">
              <Sparkles className="w-10 h-10 mx-auto mb-4 text-primary" />
              <h3 className="font-semibold mb-2">No Signup Required</h3>
              <p className="text-sm text-muted-foreground">
                Start using instantly without creating an account
              </p>
            </div>
            <div className="glass-card p-6">
              <Gift className="w-10 h-10 mx-auto mb-4 text-green-500" />
              <h3 className="font-semibold mb-2">100% Free Forever</h3>
              <p className="text-sm text-muted-foreground">
                No hidden charges, premium features, or limitations
              </p>
            </div>
            <div className="glass-card p-6">
              <QrCode className="w-10 h-10 mx-auto mb-4 text-purple-500" />
              <h3 className="font-semibold mb-2">Instant Results</h3>
              <p className="text-sm text-muted-foreground">
                Generate and download your results in seconds
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FreeTools;
